import React, { useEffect, useMemo, useState } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import { apiService } from '../services/apiService';
import { authService } from '../services/authService';
import PinballEventForm from '../components/PinballEventForm';
import PinGolfBottomNav from '../components/PinGolfBottomNav';
import {
  DSAlert,
  DSBadge,
  DSCard,
  DSIcon
} from '../design-system/primitives';
import { eventEndTime, isEventInProgress } from '../utils/eventHelpers';
import pingolfHero from '../assets/pingolf-hero.webp';
import './pingolfHeroShared.css';
import './PinGolfEvents.css';

const STATUS_CONFIG = {
  active: { label: 'Active', variant: 'success' },
  upcoming: { label: 'Scheduled', variant: 'info' },
  past: { label: 'Finalized', variant: 'warning' }
};

const getEventTiming = (event) => {
  const end = eventEndTime(event);
  if (end === null) {
    return 'upcoming';
  }

  if (isEventInProgress(event)) {
    return 'active';
  }

  return Date.now() < new Date(event.event_date).getTime() ? 'upcoming' : 'past';
};

function PinGolfEvents() {
  const navigate = useNavigate();
  const location = useLocation();
  const currentUser = authService.getCurrentUser();
  const isAdmin = currentUser?.role === 'admin';

  const [events, setEvents] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [showCreateForm, setShowCreateForm] = useState(false);
  const [pastExpanded, setPastExpanded] = useState(false);

  useEffect(() => {
    fetchPingolfEvents();
  }, []);

  useEffect(() => {
    if (location.state?.openCreate && isAdmin) {
      setShowCreateForm(true);
      navigate(location.pathname, { replace: true, state: {} });
    }
  }, [location.state, location.pathname, isAdmin, navigate]);

  const fetchPingolfEvents = async () => {
    try {
      setLoading(true);
      const response = await apiService.get('/events/pingolf/summary');
      setEvents(response.data || []);
      setError(null);
    } catch (err) {
      console.error('Error loading PinGolf events:', err);
      setError('Failed to load PinGolf events.');
    } finally {
      setLoading(false);
    }
  };

  const groupedEvents = useMemo(() => {
    const grouped = {
      active: [],
      upcoming: [],
      past: []
    };

    events.forEach((event) => {
      const timing = getEventTiming(event);
      grouped[timing].push(event);
    });

    grouped.active.sort((a, b) => new Date(a.event_date) - new Date(b.event_date));
    grouped.upcoming.sort((a, b) => new Date(a.event_date) - new Date(b.event_date));
    grouped.past.sort((a, b) => new Date(b.event_date) - new Date(a.event_date));

    return grouped;
  }, [events]);

  const formatEventDate = (eventDate) => {
    const date = new Date(eventDate);
    return date.toLocaleDateString('en-US', {
      month: 'numeric',
      day: 'numeric',
      year: 'numeric'
    });
  };

  const formatEventTime = (eventDate) => {
    const date = new Date(eventDate);
    return date.toLocaleTimeString('en-US', {
      hour: 'numeric',
      minute: '2-digit'
    });
  };

  const renderSection = (title, sectionKey, items, emptyMessage, collapsible = false) => {
    const isCollapsed = collapsible && !pastExpanded;

    return (
      <section className="pingolf-events-section">
        <div
          className={`pingolf-events-section-header ${collapsible ? 'is-collapsible' : ''}`}
          role={collapsible ? 'button' : undefined}
          tabIndex={collapsible ? 0 : undefined}
          onClick={collapsible ? () => setPastExpanded((prev) => !prev) : undefined}
          onKeyDown={collapsible ? (e) => {
            if (e.key === 'Enter' || e.key === ' ') {
              e.preventDefault();
              setPastExpanded((prev) => !prev);
            }
          } : undefined}
        >
          <h2>{title}</h2>
          <span className="pingolf-events-section-header-right">
            <DSBadge variant={STATUS_CONFIG[sectionKey].variant} appearance="outline">
              {items.length}
            </DSBadge>
            {collapsible && (
              <span className={`pingolf-events-section-toggle ${isCollapsed ? '' : 'is-open'}`} aria-hidden="true">
                <DSIcon name="chevronDown" size="sm" tone="muted" />
              </span>
            )}
          </span>
        </div>

        {!isCollapsed && (
          items.length === 0 ? (
            <DSCard
              title="Nothing here yet"
              subtitle={emptyMessage}
              variant="outlined"
              bgVariant="subtle"
              borderVariant="subtle"
              cornerVariant="lg"
            />
          ) : (
            <div className="pingolf-events-card-list">
              {items.map((event) => renderEventCard(event, sectionKey))}
            </div>
          )
        )}
      </section>
    );
  };

  const renderEventCard = (event, sectionStatus) => {
    const cardPrice = Number(event.event_metadata?.pingolf?.cardPriceDollars ?? event.event_metadata?.pingolf?.cardPrice ?? 1);
    const normalizedCardPrice = Number.isFinite(cardPrice) && cardPrice > 0 ? cardPrice : 1;
    const cardsSold = Number(event.cards_sold) || 0;
    const holeCount = Number(event.hole_count) || 0;
    const prizePool = Number(event.prize_pool) || 0;
    const status = STATUS_CONFIG[sectionStatus] || STATUS_CONFIG.upcoming;
    const stats = [
      { key: 'card-price', icon: 'dollar', iconTone: 'brand', label: 'Card Price', value: `$${normalizedCardPrice}` },
      { key: 'prize-pool', icon: 'trophy', iconTone: 'warning', label: 'Prize Pool', value: `$${prizePool}` },
      { key: 'cards-sold', icon: 'users', iconTone: 'info', label: 'Cards Sold', value: cardsSold },
      { key: 'holes', icon: 'golf', iconTone: 'success', label: 'Holes', value: holeCount }
    ];

    return (
      <div
        key={event.id}
        className="pingolf-events-card"
        role="button"
        tabIndex={0}
        onClick={() => navigate(`/events/${event.id}`)}
        onKeyDown={(eventKey) => {
          if (eventKey.key === 'Enter' || eventKey.key === ' ') {
            eventKey.preventDefault();
            navigate(`/events/${event.id}`);
          }
        }}
      >
        <DSCard
          className="pingolf-events-card"
          variant="outlined"
          bgVariant="default"
          borderVariant="subtle"
          cornerVariant="lg"
          title={event.title}
          actions={<DSIcon name="chevronRight" size="sm" tone="muted" />}
          subtitle={(
            <span className="pingolf-events-card-date" role="group" aria-label="Event status and schedule">
              <DSBadge variant={status.variant} appearance="outline" size="sm">{status.label}</DSBadge>
              <span className="pingolf-events-meta-item">
                <span className="pingolf-events-meta-icon" aria-hidden="true"><DSIcon name="calendar" size="sm" tone="muted" /></span>
                {formatEventDate(event.event_date)}
              </span>
              <span className="pingolf-events-meta-item">
                <span className="pingolf-events-meta-icon" aria-hidden="true"><DSIcon name="info" size="sm" tone="muted" /></span>
                {formatEventTime(event.event_date)}
              </span>
            </span>
          )}
          footer={(
            <div className="pingolf-events-card-stats">
              {stats.map((stat) => (
                <div key={stat.key} className="pingolf-events-stat-item">
                  <span className="pingolf-events-stat-icon" aria-hidden="true"><DSIcon name={stat.icon} size="md" tone={stat.iconTone} /></span>
                  <span className="pingolf-events-stat-text">
                    <span className="pingolf-events-stat-label">{stat.label}</span>
                    <strong>{stat.value}</strong>
                  </span>
                </div>
              ))}
            </div>
          )}
        />
      </div>
    );
  };

  if (loading) {
    return (
      <div className="pingolf-events-page bn-page-padding">
        <DSCard title="Loading PinGolf events" subtitle="Fetching the latest event summary and standings." />
        <PinGolfBottomNav onCreateEvent={() => setShowCreateForm(true)} />
      </div>
    );
  }

  return (
    <div className="pingolf-events-page bn-page-padding">
      <div className="pingolf-hero pingolf-events-hero">
        <img src={pingolfHero} alt="" className="pingolf-hero-banner" />
        <h1>PinGolf Events</h1>
      </div>

      {error ? <DSAlert variant="error" appearance="outline">{error}</DSAlert> : null}

      <div className="pingolf-events-sections">
        {renderSection('Active Events', 'active', groupedEvents.active, 'No active PinGolf events.')}
        {renderSection('Upcoming Events', 'upcoming', groupedEvents.upcoming, 'No upcoming PinGolf events.')}
        {renderSection('Past Events', 'past', groupedEvents.past, 'No past PinGolf events.', true)}
      </div>

      {showCreateForm && isAdmin && (
        <div className="modal-overlay">
          <div className="modal-content">
            <PinballEventForm
              initialPinballSubtype="pingolf"
              onClose={() => setShowCreateForm(false)}
              onSuccess={() => {
                setShowCreateForm(false);
                fetchPingolfEvents();
              }}
            />
          </div>
        </div>
      )}

      <PinGolfBottomNav />
    </div>
  );
}

export default PinGolfEvents;
